import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, CheckCircle2, Circle, Clock, LineChart, Award } from "lucide-react";
import { http, trackEvent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { celebrate } from "@/lib/confetti";
import { Card, PageTitle, btnPrimary } from "@/components/portal/ui";
import { money } from "@/lib/constants";
import { toast } from "sonner";
import { EnrolledCourses } from "@/components/portal/EnrolledCourses";
import { CourseLocked } from "@/components/portal/CourseOverview";

const TARGET_ROUTE = {
  intake: "/portal/diagnostic", report: "/portal/report", blueprint: "/portal/blueprint",
  blueprint_intake: "/portal/blueprint-intake", progress: "/portal/progress",
  guided: "/portal/guided", inhome: "/portal/inhome",
};
const PRIORITY = ["guided", "inhome_overnight", "blueprint", "diagnostic"];
const LOCK_LABEL = {
  blueprint: "Available after Clarity",
  guided: "Available with Guided Virtual Consulting",
  inhome_overnight: "Available with In-Home + Overnight",
};

// Clarity stage tracker for Clarity/Blueprint customers (guided/inhome use their own journey).
function clarityTracker(diag, clarityDone) {
  const st = diag?.state;
  const intakeDone = ["Submitted", "Awaiting Operator Review", "Under Review", "Delivered", "Viewed", "Completed"].includes(st) || clarityDone;
  const reviewDone = ["Delivered", "Viewed", "Completed"].includes(st) || clarityDone;
  const reportReady = reviewDone;
  const step = (done, current) => (done ? "done" : current ? "current" : "upcoming");
  return [
    { label: "Account Created", status: "done" },
    { label: "Clarity Intake", status: step(intakeDone, !intakeDone) },
    { label: "Consultant Review", status: step(reviewDone, intakeDone && !reviewDone) },
    { label: "Clarity Report", status: reportReady ? "current" : "upcoming" },
  ];
}

// Free sleep tracker — always available, never locked by intake/report completion or plan.
function SleepTrackerCard({ nav }) {
  const [count, setCount] = useState(null);
  const [last, setLast] = useState(null);
  useEffect(() => {
    http.get("/portal/sleep-logs").then((r) => { setCount(r.data.length); setLast(r.data[0]?.date || null); }).catch(() => setCount(0));
  }, []);
  return (
    <Card className="p-6 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="home-sleep-tracker">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-full grid place-items-center bg-[#9F634E]/10 shrink-0"><LineChart size={18} className="text-[#9F634E]" /></span>
        <div>
          <p className="text-[#1A1918] text-[15px] font-medium">Sleep Observation Tracker <span className="text-xs text-[#56705B]">· Free</span></p>
          <p className="text-[#6B665E] text-sm mt-0.5">
            Document and review your child's sleep patterns, including nighttime waking, naps, bedtime, and daily observations. Your records remain available as you continue your studies{last ? `. Last entry ${last}` : ""}.
            {count ? ` ${count} ${count === 1 ? "entry" : "entries"} logged.` : ""}
          </p>
        </div>
      </div>
      <button data-testid="home-open-sleep-tracker" onClick={() => nav("/portal/progress")} className={`${btnPrimary} shrink-0 inline-flex items-center gap-2`}>
        Open Sleep Observation Tracker <ArrowRight size={16} />
      </button>
    </Card>
  );
}

const AGE_BANDS = [
  ["expecting", "Expecting"], ["0-3", "Newborn: 0–3 months"], ["4-6", "Infant: 4–6 months"],
  ["7-12", "Infant: 7–12 months"], ["13-17", "Older infant: 13–17 months"], ["18+", "Toddler: 18+ months"],
];
const CHALLENGES = [
  ["night_waking", "Night wakings & settling (IS-201)"], ["short_naps", "Naps & daily rhythm (NS-101)"],
  ["falling_asleep", "Falling asleep & sleep associations (IS-201)"], ["bedtime", "Bedtime & early waking (IS-401)"],
  ["schedule", "Wake windows & schedules (NS-101)"], ["general", "Foundations of infant sleep (IS-101)"],
];
const SKIP_KEY = "isc_orientation_skipped";

// Optional academic orientation: child's age + subject of interest. Skippable; never required for coursework.
function WelcomeQuestions({ onSaved }) {
  const [m, setM] = useState(null);
  const [age, setAge] = useState("");
  const [chal, setChal] = useState("");
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(SKIP_KEY) === "1"; } catch (e) { return false; } });
  useEffect(() => {
    http.get("/portal/membership").then((r) => { setM(r.data); setAge(r.data.baby_age_band || ""); setChal(r.data.sleep_challenge || ""); }).catch(() => setM({}));
  }, []);
  if (!m || hidden) return null;
  if (m.baby_age_band && m.sleep_challenge) return null; // already answered
  const save = async () => {
    await http.put("/portal/membership", { baby_age_band: age || null, sleep_challenge: chal || null }).catch(() => {});
    toast.success("Saved — relevant lessons are now marked as recommended.");
    setHidden(true); onSaved && onSaved();
  };
  const skip = () => { try { localStorage.setItem(SKIP_KEY, "1"); } catch (e) {} setHidden(true); };
  const sel = "mt-1.5 w-full border border-[#E3DCCB] rounded-lg px-3 py-2.5 text-sm bg-[#FBF9F4] text-[#172A3A]";
  return (
    <Card className="p-6 sm:p-8 mb-6 border-t-2 border-t-[#BDA677]" data-testid="welcome-questions">
      <div className="eyebrow text-[#8C7444] mb-1">Optional orientation</div>
      <h2 className="font-academic font-semibold text-2xl text-[#172A3A]">Welcome to Your Academic Studies</h2>
      <p className="text-[#4F5B67] text-[15px] max-w-2xl mt-2">Your student dashboard brings together your courses, educational readings, Sleep Observation Tracker, and academic progress. Explore the subjects available through our Course Catalog, beginning with IS-101 Foundations of Infant Sleep.</p>
      <p className="text-[#6B7480] text-sm mt-3">If you wish, tell us your child's age and the subject that interests you most, and we'll highlight the most relevant lessons. You can begin your coursework without answering.</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        <div>
          <label className="text-sm text-[#172A3A] font-medium" htmlFor="welcome-age">Your child's age</label>
          <select id="welcome-age" data-testid="welcome-age" value={age} onChange={(e) => setAge(e.target.value)} className={sel}>
            <option value="">Select…</option>
            {AGE_BANDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm text-[#172A3A] font-medium" htmlFor="welcome-challenge">Subject of greatest interest</label>
          <select id="welcome-challenge" data-testid="welcome-challenge" value={chal} onChange={(e) => setChal(e.target.value)} className={sel}>
            <option value="">Select…</option>
            {CHALLENGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button data-testid="welcome-save" onClick={save} disabled={!age && !chal} className={`${btnPrimary} disabled:opacity-50`}>Save my interests</button>
        <button data-testid="welcome-skip" onClick={skip} className="text-[#4F5B67] text-sm hover:text-[#172A3A] underline-offset-4 hover:underline">Skip orientation</button>
      </div>
    </Card>
  );
}

// "My Academic Studies": IS-101 progress, where the student left off, and the next lesson.
const SP_TITLES = { "ns-101": "NS-101 Naps, Wake Windows & Daily Rhythm", "is-201": "IS-201 Night Wakings & Independent Settling", "is-301": "IS-301 Sleep Regressions & Developmental Changes", "is-401": "IS-401 Bedtime, Early Waking & the Crib Transition" };

function StudyNotice({ c, slug, nav }) {
  const f = c.final_state, tid = slug.replace("-", "");
  const go = () => nav(`/portal/resources?course=${slug}#final`);
  if (c.completed_at) return (
    <div className="mt-4 rounded-xl bg-[#172A3A] text-[#F5F2E9] p-4 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`${tid}-completed`}>
      <span className="inline-flex items-center gap-1.5 text-[#BDA677] text-xs uppercase tracking-[0.18em]"><Award size={16} /> Completed</span>
      <span className="text-sm">Completed {new Date(c.completed_at).toLocaleDateString()} · Final grade {f.grade} of {f.max} · {c.lessons.length} of {c.lessons.length} modules</span>
      <button onClick={() => nav(`/portal/resources?course=${slug}`)} className="text-sm text-[#BDA677] underline underline-offset-4" data-testid={`${tid}-completion-summary`}>View completion summary</button>
    </div>
  );
  const msg = f.state === "pending" ? "Final assessment submitted — awaiting faculty review." : f.state === "released" ? (f.passed ? "Final assessment results released — complete your remaining modules." : "Final assessment reviewed — additional work required.") : null;
  if (!msg) return null;
  return <div className="mt-4 rounded-xl border border-[#BDA677] bg-[#FBF7EC] p-3 text-sm text-[#172A3A] flex flex-wrap items-center gap-3" data-testid={`${tid}-final-notice`}>{msg}{f.state === "released" && <button onClick={go} className="text-[#7A6438] underline underline-offset-4" data-testid={`${tid}-view-feedback`}>View results & remarks</button>}</div>;
}

function NS101Card({ nav, slug = "ns-101" }) {
  const [c, setC] = useState(null);
  const tid = slug.replace("-", "");
  useEffect(() => { http.get(`/portal/courses/${slug}/study`).then((r) => setC(r.data)).catch(() => {}); }, [slug]);
  if (!c) return null;
  const done = c.lessons.filter((l) => l.status === "Completed").length;
  const next = c.lessons.find((l) => l.status !== "Completed");
  return (
    <Card className="p-6 sm:p-8 mt-4" data-testid={`${tid}-card`}>
      <div className="eyebrow text-[#8C7444] mb-1">My Courses · Enrolled · Study at your own pace</div>
      <p className="font-academic font-semibold text-2xl sm:text-3xl text-[#172A3A]">{SP_TITLES[slug]}</p>
      <div className="flex justify-between text-sm text-[#4F5B67] mt-5 mb-1.5"><span>Academic Progress</span><span data-testid={`${tid}-progress`}><span className="font-medium text-[#172A3A]">{done}</span> of {c.lessons.length} lessons{c.completed_at ? " · Completed" : ""}</span></div>
      <div className="h-1.5 bg-[#EDE7D9] rounded-full overflow-hidden"><div className="h-full bg-[#BDA677]" style={{ width: `${(done / c.lessons.length) * 100}%` }} /></div>
      <StudyNotice c={c} slug={slug} nav={nav} />
      <button data-testid={`${tid}-continue`} onClick={() => nav(next ? `/portal/resources?course=${slug}&m=${next.key}` : `/portal/resources?course=${slug}`)} className={`${btnPrimary} mt-6 inline-flex items-center gap-2`}>{c.completed_at ? "Review Course" : done === 0 && !next?.assessment ? "Begin Course" : next ? "Continue Learning" : "Final Assessment"} <ArrowRight size={16} /></button>
    </Card>
  );
}

function AcademicStudies({ nav, refreshKey }) {
  const [mods, setMods] = useState(null);
  const [access, setAccess] = useState(true);
  const [other, setOther] = useState(false);
  const [s101, setS101] = useState(null);
  useEffect(() => {
    http.get("/portal/modules").then((r) => { setAccess(r.data.is101_access); setMods(r.data.modules.filter((x) => x.unlocked && x.lesson_no).sort((a, b) => a.lesson_no - b.lesson_no)); if (r.data.is101_access) http.get("/portal/courses/is-101/study").then((s) => setS101(s.data)).catch(() => {}); }).catch(() => setMods([]));
    http.get("/portal/courses/access").then((r) => setOther(Object.values(r.data.courses || {}).some((c) => c.access))).catch(() => {});
  }, [refreshKey]);
  if (!mods) return null;
  if (!access) return (
    <section className="mb-6" data-testid="student-courses">
      <h2 className="font-academic font-semibold text-2xl text-[#172A3A] mb-3">My Academic Studies</h2>
      {!other && <p className="text-sm text-[#4F5B67] mb-3" data-testid="student-not-enrolled">You are not enrolled in a course yet. Your free student account includes the Sleep Observation Tracker, educational readings and the full Course Catalog.</p>}
      <CourseLocked compact />
      <NS101Card nav={nav} />
      <NS101Card nav={nav} slug="is-201" />
      <NS101Card nav={nav} slug="is-301" />
      <NS101Card nav={nav} slug="is-401" />
    </section>
  );
  const done = mods.filter((x) => x.status === "Completed").length;
  const current = mods.find((x) => x.status === "In Progress");
  const lastDone = [...mods].reverse().find((x) => x.status === "Completed");
  const next = mods.find((x) => x.status === "Not Started");
  const pct = mods.length ? Math.round((done / mods.length) * 100) : 0;
  const action = s101?.completed_at ? "Review Course" : done === 0 && !current ? "Begin Course" : done === mods.length ? "Final Assessment" : "Continue Learning";
  const target = current || next;
  const open = () => {
    if (target) trackEvent("module_started", { module_id: target.key }, { once: true, key: target.key });
    nav(target && !s101?.completed_at ? `/portal/resources?m=${target.key}` : "/portal/resources");
  };
  return (
    <section className="mb-6" data-testid="student-courses">
      <h2 className="font-academic font-semibold text-2xl text-[#172A3A] mb-3">My Academic Studies</h2>
      <Card className="p-6 sm:p-8" data-testid="learning-center-card">
        <div className="eyebrow text-[#8C7444] mb-1">My Courses · Enrolled · Study at your own pace</div>
        <p className="font-academic font-semibold text-2xl sm:text-3xl text-[#172A3A]">IS-101 Foundations of Infant Sleep</p>
        <div className="mt-5" data-testid="learning-progress">
          <div className="flex justify-between text-sm text-[#4F5B67] mb-1.5"><span>Academic Progress</span><span><span className="font-medium text-[#172A3A]">{done}</span> of {mods.length} lessons completed{s101?.completed_at ? " · Completed" : ""}</span></div>
          <div className="h-1.5 bg-[#EDE7D9] rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><div className="h-full bg-[#BDA677] transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
          <div data-testid="student-left-off"><div className="text-[#6B7480] text-xs uppercase tracking-[0.12em] mb-1">Where you left off</div><span className="text-[#172A3A]">{current ? current.title : lastDone ? `Completed: ${lastDone.title}` : "Not yet started"}</span></div>
          <div data-testid="student-next-lesson"><div className="text-[#6B7480] text-xs uppercase tracking-[0.12em] mb-1">Coursework & Readings · Next</div><span className="text-[#172A3A]">{next ? next.title : "No remaining lessons"}</span></div>
        </div>
        {s101 && <StudyNotice c={s101} slug="is-101" nav={nav} />}
        <div className="flex flex-wrap gap-3 mt-6">
          <button data-testid="student-start-is101" onClick={open} className={`${btnPrimary} inline-flex items-center gap-2`}>{action} <ArrowRight size={16} /></button>
          <button data-testid="open-learning-center" onClick={() => nav("/portal/resources")} className="text-[#7A6438] text-sm self-center hover:underline">All lessons in the Learning Center</button>
          <a href="/courses" data-testid="student-course-catalog" className="text-[#7A6438] text-sm self-center hover:underline">Course Catalog →</a>
        </div>
      </Card>
      <NS101Card nav={nav} />
      <NS101Card nav={nav} slug="is-201" />
      <NS101Card nav={nav} slug="is-301" />
      <NS101Card nav={nav} slug="is-401" />
    </section>
  );
}

function Tracker({ steps }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="stage-tracker">
      {steps.map((j, idx) => (
        <div key={j.label} className="flex items-center gap-2">
          <span className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full ${j.status === "done" ? "bg-[#667A66]/12 text-[#4d6b4d]" : j.status === "current" ? "bg-[#9F634E]/12 text-[#9F634E] font-medium" : "bg-[#F3EFE9] text-[#8A998C]"}`}>
            {j.status === "done" ? <CheckCircle2 size={13} /> : j.status === "current" ? <Clock size={13} /> : <Circle size={11} />}
            {j.label}{j.status === "current" ? " · now" : ""}
          </span>
          {idx < steps.length - 1 && <ArrowRight size={13} className="text-[#D8D2C6]" />}
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [lcRefresh, setLcRefresh] = useState(0);

  useEffect(() => { http.get("/portal/overview").then((r) => setData(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const mp = data?.module_progress;
    if (mp && mp.unlocked > 0 && mp.completed === mp.unlocked) {
      const key = "isc_modules_celebrated";
      if (localStorage.getItem(key) !== String(mp.unlocked)) {
        celebrate(); trackEvent("modules_all_completed", {}); localStorage.setItem(key, String(mp.unlocked));
      }
    }
  }, [data]);

  if (!data) return <div className="text-[#6B665E]">Loading…</div>;

  const { services = [], locked_products = [], clarity_completed, plan_completed } = data;
  const byKey = Object.fromEntries(services.map((s) => [s.product_key, s]));
  const primary = PRIORITY.map((k) => byKey[k]).find(Boolean);
  const firstName = user?.name ? user.name.split(" ")[0] : "";

  // No consulting services → academic student dashboard only.
  const academic = (
    <>
      <WelcomeQuestions onSaved={() => setLcRefresh((x) => x + 1)} />
      <AcademicStudies nav={nav} refreshKey={lcRefresh} />
      <EnrolledCourses />
      <SleepTrackerCard nav={nav} />
    </>
  );
  const title = <PageTitle eyebrow="Student Dashboard · Infant Sleep Consultant Near Me Education Center" title={`Welcome${firstName ? ", " + firstName : ""}`} sub="Your courses, readings, Sleep Observation Tracker and academic progress, in one place." />;
  if (!primary) {
    return <div>{title}{academic}</div>;
  }

  const stageLabel = primary.stage
    || (primary.product_key === "diagnostic" ? "Clarity"
      : primary.product_key === "blueprint" ? "Personalized Plan"
      : primary.product_name);
  const ctaText = primary.next_action || "View details";
  const ctaRoute = primary.next_target ? (TARGET_ROUTE[primary.next_target] || "/portal") : null;
  const steps = primary.journey || clarityTracker(byKey.diagnostic, clarity_completed);

  return (
    <div>
      {title}
      {academic}

      {/* PRIVATE CONSULTING — separate service area, shown only for clients with consulting services */}
      <section className="mt-10 pt-8 border-t border-[#E3DCCB]" data-testid="private-consulting-area">
        <div className="eyebrow text-[#8C7444] mb-1">Private Consulting · Separate service</div>
        <h2 className="font-academic font-semibold text-2xl text-[#172A3A] mb-4">Your consulting services</h2>
      <Card className="p-6 sm:p-8 mb-6" data-testid="home-current-stage">
        <div className="eyebrow text-[#8C7444] mb-1">Current stage</div>
        <h2 className="font-display text-2xl sm:text-3xl text-[#1A1918]">{stageLabel}</h2>
        {ctaText !== (ctaText.length < 42 ? ctaText : "Continue") && <p className="text-[#6B665E] mt-2 text-[15px] max-w-xl">{ctaText}</p>}
        {ctaRoute ? (
          <button data-testid="home-next-step" onClick={() => nav(ctaRoute)} className={`${btnPrimary} mt-5 inline-flex items-center gap-2`}>
            {ctaText.length < 42 ? ctaText : "Continue"} <ArrowRight size={16} />
          </button>
        ) : (
          <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#8a6a2e] bg-[#C49A5A]/12 rounded-full px-4 py-2" data-testid="home-status-only">
            <Clock size={14} /> {ctaText}
          </div>
        )}
        <div className="mt-6 pt-6 border-t border-[#F0ECE4]">
          <div className="eyebrow text-[#8A998C] mb-3">Your path</div>
          <Tracker steps={steps} />
        </div>
      </Card>

      {/* LOCKED FUTURE STAGES */}
      {locked_products.length > 0 && (
        <div data-testid="home-locked-stages">
          <div className="eyebrow text-[#8A998C] mb-3">Other consulting services</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {locked_products.map((p) => {
              const gated = p.key === "blueprint" && !clarity_completed;
              return (
                <Card key={p.key} className="p-5 flex items-center justify-between gap-3 opacity-90" data-testid={`home-locked-${p.key}`}>
                  <div>
                    <div className="flex items-center gap-1.5 text-[#8a6a2e] text-xs mb-1"><Lock size={12} /> {LOCK_LABEL[p.key] || "Not yet unlocked"}</div>
                    <p className="text-[#1A1918] text-[15px] font-medium">{p.name}</p>
                    <p className="text-[#8A998C] text-xs mt-0.5">{money(p.price)}</p>
                  </div>
                  {gated ? (
                    <button onClick={() => startCheckout({ product: "diagnostic", user, navigate: nav })} className="text-[#9F634E] text-sm hover:underline whitespace-nowrap shrink-0">Start Clarity →</button>
                  ) : (
                    <button onClick={() => nav(p.key === "blueprint" ? "/portal/my-blueprint" : p.key === "guided" ? "/portal/guided" : "/portal/inhome")} className="text-[#9F634E] text-sm hover:underline whitespace-nowrap shrink-0">Details →</button>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}
      </section>
    </div>
  );
}
